import { getSessionExpiryMs } from "@/utils/jwt";
import { authBus } from "./authBus";
import { renovarToken, renovarTokenSePossivel } from "./renovacaoToken";
import { CHAVES, SESSAO, storage } from "./sessionConfig";

/**
 * Controle de sessão deslizante por atividade.
 *
 * Coordena três coisas num único tick periódico:
 *  (a) atividade do usuário — grava o instante da última interação;
 *  (b) renovação proativa — troca o token antes de ele vencer;
 *  (c) inatividade — avisa e, sem resposta, encerra a sessão.
 *
 * O tick é um setInterval de baixa frequência (e não um setTimeout longo)
 * porque navegadores estrangulam timers em abas ocultas; o mesmo tick é
 * forçado no visibilitychange/focus para cobrir o intervalo perdido.
 */

const EVENTOS_ATIVIDADE: Array<keyof WindowEventMap> = [
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "wheel",
];

let intervalId: ReturnType<typeof setInterval> | null = null;
let ativo = false;
let avisoAberto = false;
/** Vida do token medida entre duas renovações; usada para calcular o limiar. */
let vidaTokenMs = SESSAO.VIDA_TOKEN_PADRAO_MS;
/** API sem POST /Login/renew: desliga a renovação proativa em vez de insistir. */
let renovacaoIndisponivel = false;
let ultimaGravacaoAtividade = 0;

const agora = () => Date.now();

const lerUltimaAtividade = (): number => {
  const valor = Number(storage.ler(CHAVES.ULTIMA_ATIVIDADE));
  return Number.isFinite(valor) && valor > 0 ? valor : agora();
};

const gravarAtividade = (instante: number) => {
  ultimaGravacaoAtividade = instante;
  storage.gravar(CHAVES.ULTIMA_ATIVIDADE, String(instante));
};

/**
 * Marca atividade do usuário, no máximo uma gravação a cada
 * ATIVIDADE_THROTTLE_MS. Fica em localStorage de propósito: atividade em uma
 * aba mantém todas as outras vivas.
 */
const registrarAtividade = (forcar = false) => {
  // Com o aviso na tela, mexer o mouse por cima do modal não pode cancelar a
  // contagem — só o botão "Continuar conectado" (que chama com forcar = true).
  if (avisoAberto && !forcar) return;

  const instante = agora();
  if (!forcar && instante - ultimaGravacaoAtividade < SESSAO.ATIVIDADE_THROTTLE_MS) {
    return;
  }
  gravarAtividade(instante);
};

const aoInteragir = () => registrarAtividade();

const fecharAviso = () => {
  if (!avisoAberto) return;
  avisoAberto = false;
  authBus.notificarAvisoInatividade(null);
};

const abrirAviso = (expiraEm: number) => {
  if (avisoAberto) return;
  avisoAberto = true;
  authBus.notificarAvisoInatividade(expiraEm);
};

/** Limiar a partir do qual vale renovar: metade da vida do token, com piso. */
const limiarRenovacao = () =>
  Math.max(vidaTokenMs * SESSAO.RENOVAR_RATIO, SESSAO.RENOVAR_MIN_RESTANTE_MS);

const tick = () => {
  if (!ativo) return;

  const user = storage.lerJson(CHAVES.USER);
  if (!user?.token) {
    // Outra aba encerrou a sessão (ou o storage foi limpo).
    authBus.notificarSessaoExpirada("outra-aba");
    return;
  }

  const instante = agora();
  const ocioso = instante - lerUltimaAtividade();

  // (c) Inatividade — o prazo é sempre absoluto para sobreviver ao throttling
  // de timers em aba oculta: a contagem não "pausa" junto com o intervalo.
  if (ocioso >= SESSAO.IDLE_LIMITE_MS + SESSAO.IDLE_AVISO_MS) {
    authBus.notificarSessaoExpirada("inatividade");
    return;
  }
  if (ocioso >= SESSAO.IDLE_LIMITE_MS) {
    abrirAviso(lerUltimaAtividade() + SESSAO.IDLE_LIMITE_MS + SESSAO.IDLE_AVISO_MS);
  } else {
    fecharAviso();
  }

  // (b) Renovação. Expiração desconhecida = fail-open: não renova e não
  // desloga; o 401 da API resolve se o token realmente estiver morto.
  const expiraEm = getSessionExpiryMs(user);
  if (expiraEm === null) return;

  const restante = expiraEm - instante;
  if (restante <= SESSAO.CLOCK_SKEW_MS) {
    authBus.notificarSessaoExpirada("expirada");
    return;
  }

  // A condição é "ainda dentro da janela de inatividade", e não "houve
  // atividade recente": como o token vive bem menos que as 4h de ociosidade,
  // parar de renovar cedo mataria a sessão ANTES do aviso — o usuário levaria
  // um 401 seco em vez do modal, e o botão "Continuar conectado" não teria o
  // que renovar.
  if (
    !renovacaoIndisponivel &&
    restante <= limiarRenovacao() &&
    ocioso < SESSAO.IDLE_LIMITE_MS
  ) {
    renovarTokenSePossivel().then((resultado) => {
      if (resultado.status === "indisponivel") {
        renovacaoIndisponivel = true;
      } else if (resultado.status === "ok") {
        atualizarVidaToken(resultado.user);
      }
    });
  }
};

const atualizarVidaToken = (user: any) => {
  const expiraEm = getSessionExpiryMs(user);
  if (expiraEm !== null) {
    vidaTokenMs = Math.max(expiraEm - agora(), SESSAO.RENOVAR_MIN_RESTANTE_MS);
  }
};

const aoMudarStorage = (evento: StorageEvent) => {
  if (!ativo) return;
  if (evento.key === CHAVES.USER) {
    if (evento.newValue === null) {
      // Logout em outra aba.
      authBus.notificarSessaoExpirada("outra-aba");
      return;
    }
    // Renovação feita em outra aba: o token novo já vale para esta.
    try {
      atualizarVidaToken(JSON.parse(evento.newValue));
    } catch {
      // Valor ilegível: o próximo tick reavalia pelo storage.
    }
  }
};

const aoVoltarParaAba = () => {
  // Voltar para a aba não conta como atividade (senão bastaria alternar de aba
  // para zerar horas de ociosidade) — apenas força a reavaliação perdida
  // enquanto os timers estavam estrangulados.
  tick();
};

export const sessionManager = {
  /** Idempotente. Devolve a função de parada, para uso direto no useEffect. */
  iniciar() {
    if (ativo) return () => sessionManager.parar();

    ativo = true;
    avisoAberto = false;
    renovacaoIndisponivel = false;
    authBus.resetar();

    const user = storage.lerJson(CHAVES.USER);
    if (user) atualizarVidaToken(user);

    // Uma sessão restaurada do storage não tem "última atividade" confiável:
    // abrir o sistema é, ele próprio, atividade.
    gravarAtividade(agora());

    EVENTOS_ATIVIDADE.forEach((evento) =>
      window.addEventListener(evento, aoInteragir, { passive: true, capture: true })
    );
    window.addEventListener("storage", aoMudarStorage);
    window.addEventListener("focus", aoVoltarParaAba);
    document.addEventListener("visibilitychange", aoVoltarParaAba);

    intervalId = setInterval(tick, SESSAO.TICK_MS);

    return () => sessionManager.parar();
  },

  parar() {
    if (!ativo) return;
    ativo = false;
    avisoAberto = false;

    EVENTOS_ATIVIDADE.forEach((evento) =>
      window.removeEventListener(evento, aoInteragir, { capture: true } as any)
    );
    window.removeEventListener("storage", aoMudarStorage);
    window.removeEventListener("focus", aoVoltarParaAba);
    document.removeEventListener("visibilitychange", aoVoltarParaAba);

    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  },

  registrarAtividade(forcar = false) {
    registrarAtividade(forcar);
  },

  /** Ação do botão "Continuar conectado" do aviso de inatividade. */
  async continuarSessao() {
    fecharAviso();
    registrarAtividade(true);
    const resultado = await renovarToken();
    if (resultado.status === "ok") {
      atualizarVidaToken(resultado.user);
    } else if (resultado.status === "invalida") {
      authBus.notificarSessaoExpirada("expirada");
    }
  },

  /** Executa um tick agora (usado pelos testes e ao voltar para a aba). */
  verificarAgora() {
    tick();
  },

  estaAtivo() {
    return ativo;
  },
};
